import { Beneficiary, ContractTenant } from '../../../../common/validations'
import { BeneficiaryType } from './initialValues'
import {
  countryCodeFromIban,
  DEFAULT_BANK_COUNTRY_CODE,
} from '../utils/bankCountry'

const tenantIsPerson = (tenant: ContractTenant) =>
  'firstName' in tenant && 'lastName' in tenant

interface Props {
  initiatingTenantId: number // The tenant creating the termination request
  tenants: ContractTenant[]
  beneficiaries: Beneficiary[]
}

export const calculateRemainingPercentage = (
  beneficiaries: BeneficiaryType[]
): number => {
  const filledBeneficiaries = beneficiaries.filter((b) => {
    const hasName = !!b.beneficiaryName?.trim()
    const hasIban = !!b.iban?.trim()
    return hasName || hasIban
  })

  const totalUsed = filledBeneficiaries.reduce((sum, b) => {
    return sum + (b.returnPercentage ?? 0)
  }, 0)

  return Math.max(0, 100 - totalUsed)
}

export const calculateRemainingRooReturnPercentage = (
  beneficiaries: BeneficiaryType[]
): number | null => {
  const configuredPercentages = beneficiaries.flatMap(
    ({ removeExistingBeneficiary, rooReturnPercentage }) =>
      removeExistingBeneficiary || rooReturnPercentage === null
        ? []
        : [rooReturnPercentage]
  )

  return configuredPercentages.length === 0
    ? null
    : Math.max(
        0,
        100 -
          configuredPercentages.reduce((sum, percentage) => sum + percentage, 0)
      )
}

export const newBeneficiary = (): BeneficiaryType => ({
  existingBeneficiaryId: null,
  removeExistingBeneficiary: false,
  contractToTenantAssociationId: null,
  beneficiaryName: null,
  iban: null,
  bic: null,
  bankCountryCode: DEFAULT_BANK_COUNTRY_CODE,
  returnPercentage: 100,
  rooReturnPercentage: null,
  phoneNumber: null,
  email: null,
})

const makeEmptyIfNotNameAndIban = (
  beneficiary: BeneficiaryType
): BeneficiaryType => {
  const hasName = !!beneficiary.beneficiaryName?.trim()
  const hasIban = !!beneficiary.iban?.trim()

  // Completely empty row -> keep returnPercentage as is (preserve default 100)
  if (!hasName && !hasIban) {
    return beneficiary
  }

  // Name is set but IBAN is missing -> reset percentage to 0
  if (!hasIban) {
    return {
      ...beneficiary,
      returnPercentage: 0,
    }
  }

  // Both are present -> no changes
  return beneficiary
}

export const initialBeneficiaries = ({
  initiatingTenantId,
  tenants,
  beneficiaries,
}: Props): BeneficiaryType[] => {
  const initiatingTenant = tenants.find((t) => t.id === initiatingTenantId)

  if (!initiatingTenant) {
    throw new Error('Initiating tenant not found')
  }

  if (!tenantIsPerson(initiatingTenant)) {
    throw new Error('Company tenants are not supported')
  }

  const existingInitiatorBeneficiary = beneficiaries.find(
    (b) => b.tenantId === initiatingTenant.id
  )

  const otherBeneficiaries = beneficiaries
    .filter((b) => b.tenantId !== initiatingTenantId)
    .map((b) => {
      const correspondingTenant = tenants.find((t) => t.id === b.tenantId)
      return {
        ...newBeneficiary(),
        existingBeneficiaryId: b.id,
        beneficiaryName: b.beneficiaryName,
        iban: b.iban,
        bic: b.iban.match(/^\s*FI/i) ? null : b.bic,
        bankCountryCode:
          countryCodeFromIban(b.iban) ?? DEFAULT_BANK_COUNTRY_CODE,
        returnPercentage: b.returnPercentage
          ? b.returnPercentage
          : beneficiaries.length === 1
            ? 100
            : 0,
        rooReturnPercentage: b.rooReturnPercentage,
        phoneNumber: correspondingTenant
          ? correspondingTenant.phoneNumber
          : null,
        email: correspondingTenant ? correspondingTenant.email : null,
      }
    })

  const initiatorBeneficiary = existingInitiatorBeneficiary
    ? {
        ...newBeneficiary(),
        existingBeneficiaryId: existingInitiatorBeneficiary.id,
        beneficiaryName: existingInitiatorBeneficiary.beneficiaryName,
        iban: existingInitiatorBeneficiary.iban,
        bic: existingInitiatorBeneficiary.iban.match(/^\s*FI/i)
          ? null
          : existingInitiatorBeneficiary.bic,
        bankCountryCode:
          countryCodeFromIban(existingInitiatorBeneficiary.iban) ??
          DEFAULT_BANK_COUNTRY_CODE,
        returnPercentage: existingInitiatorBeneficiary.returnPercentage
          ? existingInitiatorBeneficiary.returnPercentage
          : otherBeneficiaries.length === 0
            ? 100
            : 0,
        rooReturnPercentage: existingInitiatorBeneficiary.rooReturnPercentage,
        phoneNumber: initiatingTenant.phoneNumber,
        email: initiatingTenant.email,
      }
    : {
        ...newBeneficiary(),
        phoneNumber: initiatingTenant.phoneNumber,
        email: initiatingTenant.email,
        returnPercentage: otherBeneficiaries.length > 0 ? 0 : 100,
      }

  return [initiatorBeneficiary, ...otherBeneficiaries].map(
    makeEmptyIfNotNameAndIban
  )
}
